import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Paper, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const renderParagraphs = (text) => {
  if (!text) return null;
  return text.split(/\n\n+/).map((p, idx) => (
    <Typography paragraph key={idx} sx={{ whiteSpace: 'pre-line' }}>
      {p}
    </Typography>
  ));
};

const formatTimestamp = (ts) => {
  if (!ts) return '[Insert Date]';
  try {
    const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
    return d.toLocaleDateString();
  } catch (e) {
    return '[Insert Date]';
  }
};

const TermsPage = ({ docId = 'termsCustomer', defaultTitle = 'Terms and Conditions' }) => {
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const ref = doc(db, 'settings', docId);
        const snap = await getDoc(ref);
        if (!mounted) return;
        if (snap.exists()) {
          const data = snap.data();
          if (data.sections && Array.isArray(data.sections)) setPolicy(data);
          else if (typeof data.content === 'string') setPolicy({ sections: [{ id: 'legacy', heading: defaultTitle, content: data.content }], lastUpdated: data.lastUpdated || null, title: data.title || defaultTitle });
          else setPolicy({ sections: [{ id: 'default', heading: defaultTitle, content: 'Please review these terms carefully.' }], lastUpdated: data.lastUpdated || null, title: data.title || defaultTitle });
        } else {
          setPolicy({ sections: [{ id: 'default', heading: defaultTitle, content: 'Please review these terms carefully.' }], lastUpdated: null, title: defaultTitle });
        }
      } catch (err) {
        console.error('Failed to load terms', err);
        if (mounted) setPolicy({ sections: [{ id: 'default', heading: defaultTitle, content: 'Please review these terms carefully.' }], lastUpdated: null, title: defaultTitle });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [docId, defaultTitle]);

  // Basic sections renderer (simple paragraphs and optional blocks)
  const renderSections = (sections) => {
    if (!sections || !Array.isArray(sections)) return null;
    return sections.map((s, idx) => (
      <Accordion key={s.id || idx} sx={{ mb: 2, borderRadius: 2, backgroundColor: '#F3F9FF', boxShadow: 'none', border: '1px solid #E6F0FF', '&:before': { display: 'none' } }} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#E8F0FF', color: '#2b7be4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
              <InfoOutlinedIcon sx={{ color: '#2b7be4', fontSize: 18 }} />
            </Box>
            <Typography variant="h6" sx={{ color: '#2b7be4', fontWeight: 700 }}>{`${idx + 1}. ${s.heading || ''}`}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {s.blocks && Array.isArray(s.blocks) ? s.blocks.map((b, bi) => (
            b.type === 'subheading' ? (
              <Typography key={b.id || bi} variant="subtitle1" sx={{ fontWeight: 700, color: '#2b7be4', mb: 1 }}>{`${idx + 1}.${bi + 1} ${b.content}`}</Typography>
            ) : (
              <Typography key={b.id || bi} paragraph sx={{ whiteSpace: 'pre-line' }}>{b.content}</Typography>
            )
          )) : renderParagraphs(s.content)}
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={6} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ background: 'linear-gradient(90deg,#2b7be4,#5b3bd6)', color: '#fff', p: 3.5 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <InfoOutlinedIcon sx={{ color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{policy?.title || defaultTitle}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>{policy?.description || 'Welcome to My Local Force. Please review these terms carefully.'}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.95, mt: 1, display: 'block' }}>
                Last Updated: {policy ? formatTimestamp(policy.lastUpdated) : '[Insert Date]'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {renderSections(policy?.sections || [{ id: 'default', heading: defaultTitle, content: 'Please review these terms carefully.' }])}
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsPage;
